import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listSuppliers } from '../suppliers/api';
import { listPaymentTerms } from '../paymentTerms/api';
import {
  createPurchaseOrder,
  listShipTos,
  shipToSummary,
  type PurchaseOrderDto,
} from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  onCreated: (order: PurchaseOrderDto) => void;
}

/**
 * Raising an order: the three things that cannot change afterwards, plus the few that are easier
 * to set now than to come back for.
 *
 * No PO number field. It comes from the PURCHASE_ORDER sequence, which is the whole reason nobody
 * has to keep a spreadsheet of which numbers are used.
 *
 * The supplier list is narrowed to the chosen warehouse: the server refuses an order placed with a
 * supplier who is not assigned there (53723), and offering the choice only to reject it is a worse
 * way to explain the rule than not offering it.
 */
export function NewPurchaseOrderModal({ opened, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [supplierId, setSupplierId] = useState('');
  const [expected, setExpected] = useState('');
  const [paymentTermId, setPaymentTermId] = useState<string | null>(null);
  const [shipToId, setShipToId] = useState<string | null>(null);
  const [attentionTo, setAttentionTo] = useState('');
  const [externalPoNumber, setExternalPoNumber] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // An order is raised for the warehouse the session is working in. There was a picker here; the
  // API refuses a body naming a different warehouse from the header, and it is the right refusal —
  // ordering into a site you are not standing in is how goods arrive where nobody expects them.
  const warehouseId = useCurrentWarehouseId();

  // Refetched per warehouse, because who you can order from is a property of the site.
  const suppliers = useQuery({
    queryKey: ['suppliers', 'for-warehouse', warehouseId || 'none'] as const,
    queryFn: ({ signal }) => listSuppliers({ warehouseId }, signal),
    enabled: opened && Boolean(warehouseId),
  });

  const shipTos = useQuery({
    queryKey: ['ship-tos'] as const,
    queryFn: ({ signal }) => listShipTos(false, signal),
    enabled: opened,
  });

  const terms = useQuery({
    queryKey: ['payment-terms'] as const,
    queryFn: ({ signal }) => listPaymentTerms(false, signal),
    enabled: opened,
  });

  useEffect(() => {
    if (!opened) return;
    setSupplierId('');
    setExpected('');
    setPaymentTermId(null);
    setShipToId(null);
    setAttentionTo('');
    setExternalPoNumber('');
    setErrors({});
    setSubmitError(null);
  }, [opened]);

  const selectedSupplier = useMemo(
    () => (suppliers.data ?? []).find((s) => s.supplierId === supplierId) ?? null,
    [suppliers.data, supplierId],
  );

  // The supplier's own terms are the default, which is what they are for.
  useEffect(() => {
    if (selectedSupplier?.paymentTermId) setPaymentTermId(selectedSupplier.paymentTermId);
  }, [selectedSupplier]);

  const mutation = useMutation({
    mutationFn: () =>
      createPurchaseOrder({
        warehouseId,
        supplierId,
        expectedReceiptDateUtc: expected ? `${expected}T00:00:00Z` : null,
        paymentTermId,
        shipToId,
        attentionTo: attentionTo.trim() || null,
        externalPoNumber: externalPoNumber.trim() || null,
      }),
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      onCreated(order);
      onClose();
    },
    onError: (err) => {
      if (!(err instanceof ApiError)) {
        setSubmitError(t('purchaseOrders.errors.createFailed'));
        return;
      }
      const type = err.problem.type ?? '';
      // Both name the supplier and the warehouse, so the server's own detail is the useful text.
      if (type.includes('supplier_not_assigned') || type.includes('supplier_inactive')) {
        setSubmitError(err.problem.detail ?? err.problem.title ?? t('purchaseOrders.errors.createFailed'));
        return;
      }
      setSubmitError(err.problem.detail ?? err.problem.title ?? t('purchaseOrders.errors.createFailed'));
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: Record<string, string> = {};
    if (!warehouseId) next.warehouseId = t('purchaseOrders.errors.warehouseRequired');
    if (!supplierId) next.supplierId = t('purchaseOrders.errors.supplierRequired');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    mutation.mutate();
  }

  const noSuppliersHere =
    Boolean(warehouseId) && suppliers.isSuccess && (suppliers.data ?? []).length === 0;

  return (
    <AppModal opened={opened} onClose={onClose} title={t('purchaseOrders.new')} width="md">
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('purchaseOrders.numberAllocated')}
          </Text>


          <Select
            label={t('purchaseOrders.supplier')}
            description={t('purchaseOrders.supplierAssignedHint')}
            placeholder={
              warehouseId ? t('purchaseOrders.supplierPlaceholder') : t('purchaseOrders.pickWarehouseFirst')
            }
            data={(suppliers.data ?? [])
              .filter((s) => s.isActive)
              .map((s) => ({ value: s.supplierId, label: `${s.supplierNumber} — ${s.supplierName}` }))}
            value={supplierId || null}
            onChange={(v) => setSupplierId(v ?? '')}
            error={errors.supplierId}
            disabled={!warehouseId || suppliers.isLoading}
            searchable
            allowDeselect={false}
            required
          />

          {noSuppliersHere && (
            <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('purchaseOrders.noSuppliersForWarehouse')}
            </Alert>
          )}

          <TextInput
            type="date"
            label={t('purchaseOrders.expected')}
            description={t('purchaseOrders.expectedHint')}
            value={expected}
            onChange={(e) => setExpected(e.currentTarget.value)}
          />

          <Select
            label={t('purchaseOrders.shipTo')}
            description={t('purchaseOrders.shipToHint')}
            placeholder={t('purchaseOrders.shipToPlaceholder')}
            data={(shipTos.data ?? []).map((s) => ({
              value: s.purchaseOrderShipToId,
              label: shipToSummary(s),
            }))}
            value={shipToId}
            onChange={setShipToId}
            disabled={shipTos.isLoading}
            searchable
            clearable
          />

          <Select
            label={t('purchaseOrders.paymentTerm')}
            placeholder={t('purchaseOrders.paymentTermPlaceholder')}
            data={(terms.data ?? []).map((p) => ({
              value: p.paymentTermId,
              label: `${p.paymentTermCode} — ${p.displayName}`,
            }))}
            value={paymentTermId}
            onChange={setPaymentTermId}
            disabled={terms.isLoading}
            searchable
            clearable
          />

          <TextInput
            label={t('purchaseOrders.attentionTo')}
            value={attentionTo}
            onChange={(e) => setAttentionTo(e.currentTarget.value)}
            maxLength={150}
          />

          <TextInput
            label={t('purchaseOrders.externalPoNumber')}
            description={t('purchaseOrders.externalPoHint')}
            value={externalPoNumber}
            onChange={(e) => setExternalPoNumber(e.currentTarget.value)}
            maxLength={50}
          />

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={mutation.isPending}>
              {t('purchaseOrders.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
